import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useLayoutStore } from '@shared/hooks/useLayoutStore';
import { usePanelTitle } from './usePanelTitle';

const PANEL_WIDTH = 400;

interface PanelContainerProps {
  title: string;
  children: React.ReactNode;
}

export default function PanelContainer({ title, children }: PanelContainerProps) {
  const panelPinned = useLayoutStore((s) => s.panelPinned);
  const togglePanelPin = useLayoutStore((s) => s.togglePanelPin);
  const setActivePanel = useLayoutStore((s) => s.setActivePanel);
  const { panelTitle } = usePanelTitle();

  return (
    <Box
      data-testid="panel-container"
      sx={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ...(panelPinned
          ? { position: 'relative' }
          : { position: 'absolute', left: 0, top: 0, zIndex: 'drawer', boxShadow: 4 }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          gap: 1,
        }}
      >
        {panelTitle ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>{panelTitle}</Box>
        ) : (
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            {title}
          </Typography>
        )}
        <Tooltip title={panelPinned ? 'Unpin panel' : 'Pin panel'}>
          <IconButton size="small" onClick={togglePanelPin} aria-label="Toggle pin">
            {panelPinned ? (
              <PushPinIcon fontSize="small" />
            ) : (
              <PushPinOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close panel">
          <IconButton size="small" onClick={() => setActivePanel(null)} aria-label="Close panel">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>{children}</Box>
    </Box>
  );
}
